import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { licenseApiService } from '@/services/licenseApiService'
import { HttpError } from '@/lib/httpError'

interface IssuanceCodeModalProps {
  open: boolean
  // What the code is about to allow, so the dialog can say so.
  action: 'issue' | 'send'
  onConfirmed: (confirmedUntil: Date) => void
  onCancel: () => void
}

/**
 * Asks for a code from the authenticator app before a license is signed or
 * mailed (BAFTT-13).
 *
 * The sign-in code may be hours old. One confirmed here covers this session for
 * a few minutes, so issuing a license and emailing it take one code, not two.
 */
export default function IssuanceCodeModal({ open, action, onConfirmed, onCancel }: IssuanceCodeModalProps) {
  const { t } = useTranslation('plans')
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setCode('')
      setError('')
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  const confirm = async () => {
    if (isLoading || code.length !== 6) return
    setIsLoading(true)
    setError('')
    try {
      const res = await licenseApiService.confirmIssuance(code)
      onConfirmed(new Date(res.confirmed_until))
    } catch (err) {
      setCode('')
      if (err instanceof HttpError && err.code === 'TOTP_CODE_INVALID') {
        setError(t('issueModal.confirmCode.invalid', 'That code is not correct. Try the one now on your app.'))
      } else if (err instanceof HttpError && err.code === 'TOTP_CODE_REUSED') {
        setError(t('issueModal.confirmCode.reused', 'That code was already used. Wait for the next one.'))
      } else if (err instanceof HttpError && err.code === 'STEP_UP_LOCKED') {
        setError(t('issueModal.confirmCode.locked', 'Too many wrong codes. Try again in 15 minutes.'))
      } else {
        setError(err instanceof Error && err.message ? err.message : t('issueModal.errors.generic'))
      }
      setTimeout(() => inputRef.current?.focus(), 0)
    } finally {
      setIsLoading(false)
    }
  }

  const close = () => {
    if (!isLoading) onCancel()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-primary">
                {t('issueModal.confirmCode.heading', 'Confirm it is you')}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {action === 'send'
                  ? t('issueModal.confirmCode.subtitleSend', 'Needed before a license is emailed')
                  : t('issueModal.confirmCode.subtitleIssue', 'Needed before a license is signed')}
              </p>
            </div>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
              aria-label={t('common:close', 'Close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void confirm()
          }}
        >
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" role="alert">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                {t(
                  'issueModal.confirmCode.intro',
                  'Enter the 6-digit code from your authenticator app. It covers this session for 5 minutes.'
                )}
              </p>
            </div>

            <div>
              <label htmlFor="issuance-code" className="block text-sm font-medium text-gray-700 mb-1">
                {t('issueModal.confirmCode.label', 'Authenticator code')}
              </label>
              <input
                id="issuance-code"
                ref={inputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-[0.5em]"
                placeholder="000000"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isLoading}
            >
              {t('common:cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>{t('issueModal.confirmCode.confirm', 'Confirm')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
